import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * First Light — a warm, one-time 5-step setup guide for a new HearthHouse
 * household. Appears automatically the first time a signed-up user lands
 * on any authenticated route, and is skippable at any point.
 *
 * The tour is entirely presentational — no data mutation happens here.
 * Each step's CTA navigates the user to the relevant module (Profile,
 * Vendors, Staples, Family Table) where the *actual* setup happens.
 * Progress + completion are persisted via `PATCH /api/auth/onboarding`
 * so the tour never fires twice for the same account.
 *
 * Trigger contract:
 *   • New signups get `onboarding_completed: false` on the user record.
 *   • Legacy users (field absent) are treated as already-onboarded, so
 *     the tour does NOT fire retroactively for them.
 *
 * v27.28 — Fixes the "buttons don't work on iPhone / step jumps two ahead"
 * bug. The auto-open effect now runs at most once per mount (via
 * hasAutoOpenedRef), and primary() no longer advances the local step
 * *before* navigating away — otherwise React re-opens the modal at the
 * next step on the destination page and the user never sees the setup
 * page they were sent to.
 */
export default function OnboardingTour({ forceOpen = false, onDone }) {
  const { user, updateOnboarding } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  // Guards against the useEffect below re-opening the modal every time
  // ``user`` state refreshes (which happens after every PATCH we make).
  const hasAutoOpenedRef = useRef(false);
  const dismissedRef = useRef(false);
  // Track whether we've already responded to the current ``forceOpen``
  // signal — so Replay First Light doesn't reset the step back to 0
  // every time the user object refreshes mid-tour.
  const lastForceOpenRef = useRef(false);

  useEffect(() => {
    // Handle "Replay First Light" — fires exactly ONCE per rising edge of
    // ``forceOpen``. Subsequent renders (caused by ``user`` refreshing
    // after each PATCH) must not reset the step back to 0.
    if (forceOpen && !lastForceOpenRef.current) {
      setStep(0);
      setOpen(true);
      hasAutoOpenedRef.current = true;
      dismissedRef.current = false;
      lastForceOpenRef.current = true;
      return;
    }
    if (!forceOpen) {
      lastForceOpenRef.current = false;
    }
    // Auto-open exactly ONCE per component mount. Subsequent user-object
    // refreshes (which happen after every onboarding PATCH) must NOT
    // re-open the modal — otherwise the tour reappears on the destination
    // page and clobbers the very setup screen we just navigated to.
    if (hasAutoOpenedRef.current || dismissedRef.current) return;
    if (!user || !user.email) return;
    if (user.onboarding_completed === false) {
      setStep(user.onboarding_step ?? 0);
      setOpen(true);
      hasAutoOpenedRef.current = true;
    }
  }, [user, forceOpen]);

  if (!open) return null;

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  // Fire-and-forget server sync. Errors are non-fatal — the modal already
  // advanced locally, and the worst case is the user seeing the same step
  // if they reload before the request lands.
  const persistBackground = (patch) => {
    updateOnboarding(patch).catch(() => { /* swallow — non-blocking */ });
  };

  const goTo = (next) => {
    setStep(next);
    persistBackground({ step: next });
  };

  const closeTour = () => {
    dismissedRef.current = true;
    setOpen(false);
    onDone && onDone();
  };

  const primary = async () => {
    if (busy) return;
    setBusy(true);

    try {
      if (isLast) {
        closeTour();
        persistBackground({ completed: true });
        if (current.href) nav(current.href);
        return;
      }

      const next = step + 1;

      if (current.href) {
        // CTA has a destination page (Cuisines, Vendors, Staples, etc.).
        // 1) Close the modal FIRST so the destination page is visible.
        // 2) Persist the advanced step in the background — if the user
        //    replays the tour later, it resumes at the next card.
        // 3) Navigate. We do NOT locally setStep() here because the
        //    modal is closing and the auto-open ref prevents it from
        //    springing back up on the target page.
        closeTour();
        persistBackground({ step: next });
        nav(current.href);
      } else {
        // No destination — just advance in-modal (Welcome step).
        setStep(next);
        persistBackground({ step: next });
      }
    } finally {
      // Release busy immediately so a follow-up tap on the (now-visible)
      // destination page never encounters a stale disabled state.
      setBusy(false);
    }
  };

  const skip = () => {
    if (busy) return;
    closeTour();
    persistBackground({ completed: true });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-hearth-ink/70 backdrop-blur-sm p-4 md:p-8 overflow-y-auto"
      data-testid="onboarding-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      // iOS Safari — allow the modal to scroll if the content overflows the
      // dynamic viewport, e.g. when the URL bar is showing.
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {/* Card */}
      <div
        className="relative w-full max-w-2xl my-auto rounded-3xl bg-hearth-paper text-hearth-ink shadow-2xl overflow-hidden"
        style={{ boxShadow: "0 30px 80px -20px rgba(60,45,30,0.55)" }}
      >
        {/* Warm glow header strip */}
        <div
          className="h-2"
          style={{
            background:
              "linear-gradient(90deg, #B45A37 0%, #d68a5a 45%, #B45A37 100%)",
          }}
        />

        {/* Skip (top-right) */}
        <button
          data-testid="onboarding-skip"
          onClick={skip}
          disabled={busy}
          type="button"
          className="absolute top-4 right-4 p-2 rounded-full text-hearth-mute hover:text-hearth-ink hover:bg-hearth-stone/60 transition"
          aria-label="Skip setup"
          title="Skip for now — you can replay this from Profile"
        >
          <X size={18} />
        </button>

        <div className="px-6 sm:px-8 md:px-14 pt-10 pb-8 md:pt-14 md:pb-14 flex flex-col items-center text-center min-h-[440px] md:min-h-[480px]">
          {/* Eyebrow */}
          <div className="caption text-hearth-clay mb-3" style={{ letterSpacing: "0.32em" }}>
            FIRST LIGHT · STEP {step + 1} OF {STEPS.length}
          </div>

          {/* Illustration */}
          <div className="mb-6 w-24 h-24 md:w-28 md:h-28 flex items-center justify-center">
            {current.illustration}
          </div>

          {/* Title */}
          <h2
            id="onboarding-title"
            className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-hearth-ink leading-tight mb-4"
          >
            {current.title}
          </h2>

          {/* Body */}
          <p className="text-hearth-mute leading-relaxed max-w-lg text-[14px] sm:text-[15px] md:text-base italic font-serif" style={{ fontFamily: "'Fraunces','Playfair Display',serif" }}>
            {current.body}
          </p>

          <div className="flex-1" />

          {/* Progress dots */}
          <div className="flex items-center gap-2 mt-8 mb-6" role="tablist" aria-label="Setup progress">
            {STEPS.map((_, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <button
                  key={i}
                  data-testid={`onboarding-dot-${i}`}
                  type="button"
                  onClick={() => i <= step && goTo(i)}
                  disabled={i > step}
                  aria-label={`Step ${i + 1}${active ? " (current)" : done ? " (done)" : ""}`}
                  className={`transition-all rounded-full ${
                    active
                      ? "w-8 h-2 bg-hearth-clay"
                      : done
                      ? "w-2 h-2 bg-hearth-clay/50 cursor-pointer hover:bg-hearth-clay/80"
                      : "w-2 h-2 bg-hearth-line cursor-not-allowed"
                  }`}
                />
              );
            })}
          </div>

          {/* CTA — column on mobile so the primary button never gets
              pushed off-screen on small viewports (iPhone SE etc.). */}
          <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full sm:w-auto">
            {!isLast && (
              <button
                data-testid="onboarding-later"
                type="button"
                onClick={() => goTo(step + 1)}
                disabled={busy}
                className="text-xs text-hearth-mute hover:text-hearth-ink transition italic"
              >
                Not now — next step
              </button>
            )}
            <button
              data-testid="onboarding-primary"
              type="button"
              onClick={primary}
              disabled={busy}
              className="px-8 py-3 rounded-full bg-hearth-clay text-hearth-cream text-sm font-medium tracking-wide shadow-md hover:bg-hearth-clayDark transition disabled:opacity-60 min-w-[220px] w-full sm:w-auto"
            >
              {current.cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------ Illustrations ---
// Tiny hand-drawn-ish SVGs — hearth-clay ink on parchment, no external assets.

const HomeIcon = (
  <svg viewBox="0 0 96 96" width="96" height="96" fill="none" aria-hidden="true">
    <path d="M18 46 L48 22 L78 46 V78 A4 4 0 0 1 74 82 H22 A4 4 0 0 1 18 78 Z"
          stroke="#B45A37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="#fff8ec"/>
    <path d="M40 82 V60 H56 V82" stroke="#B45A37" strokeWidth="2.5" strokeLinejoin="round" fill="#f5e6c8"/>
    <circle cx="48" cy="42" r="4" fill="#B45A37"/>
    {/* Warm rays */}
    <path d="M48 14 V6 M28 22 L22 16 M68 22 L74 16" stroke="#B45A37" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CuisineIcon = (
  <svg viewBox="0 0 96 96" width="96" height="96" fill="none" aria-hidden="true">
    {/* Bowl */}
    <ellipse cx="48" cy="62" rx="34" ry="8" fill="#8a4c2a"/>
    <path d="M14 62 A34 20 0 0 0 82 62 Z" fill="#c9885a"/>
    <ellipse cx="48" cy="62" rx="28" ry="6" fill="#4a2812"/>
    {/* Steam curls */}
    <path d="M36 32 C36 22, 44 22, 44 12" stroke="#B45A37" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M52 32 C52 22, 60 22, 60 12" stroke="#B45A37" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    {/* Spice mound */}
    <path d="M28 58 Q48 42 68 58 Z" fill="#e8a828"/>
    <ellipse cx="42" cy="52" rx="6" ry="2" fill="#fff8ec" opacity="0.4"/>
  </svg>
);

const ShopIcon = (
  <svg viewBox="0 0 96 96" width="96" height="96" fill="none" aria-hidden="true">
    {/* Awning */}
    <path d="M14 30 L30 16 L66 16 L82 30 Z" fill="#B45A37" stroke="#8f3f22" strokeWidth="2"/>
    <path d="M22 30 L22 22 M32 30 L32 18 M42 30 L42 18 M52 30 L52 18 M62 30 L62 18 M72 30 L72 22"
          stroke="#8f3f22" strokeWidth="1" opacity="0.5"/>
    {/* Storefront */}
    <rect x="18" y="30" width="60" height="50" fill="#fff8ec" stroke="#8a7461" strokeWidth="2"/>
    <rect x="26" y="40" width="16" height="20" fill="#f5e6c8" stroke="#8a7461" strokeWidth="1.5"/>
    <rect x="54" y="40" width="16" height="20" fill="#f5e6c8" stroke="#8a7461" strokeWidth="1.5"/>
    <rect x="40" y="60" width="16" height="20" fill="#8a4c2a" stroke="#4a2812" strokeWidth="1.5"/>
    <circle cx="53" cy="70" r="1.2" fill="#e8b98a"/>
    {/* Location pin above */}
    <path d="M48 4 C43 4 40 8 40 12 C40 16 48 22 48 22 C48 22 56 16 56 12 C56 8 53 4 48 4 Z"
          fill="#B45A37"/>
    <circle cx="48" cy="12" r="2.5" fill="#fff8ec"/>
  </svg>
);

const PantryIcon = (
  <svg viewBox="0 0 96 96" width="96" height="96" fill="none" aria-hidden="true">
    {/* Wooden drawer */}
    <rect x="8" y="22" width="80" height="56" rx="6" fill="#4a2812" stroke="#170a03" strokeWidth="2"/>
    <rect x="12" y="26" width="72" height="48" rx="4" fill="#3c2416"/>
    {/* Row of three bowls (top-down) */}
    <g transform="translate(6, 6)">
      <ellipse cx="24" cy="42" rx="12" ry="4" fill="#8a4c2a"/>
      <path d="M14 40 Q24 28 34 40 Z" fill="#f7c530"/>
      <ellipse cx="48" cy="42" rx="12" ry="4" fill="#8a4c2a"/>
      <path d="M38 40 Q48 26 58 40 Z" fill="#c73920"/>
      <ellipse cx="72" cy="42" rx="12" ry="4" fill="#8a4c2a"/>
      <path d="M62 40 Q72 30 82 40 Z" fill="#7A8B5A"/>
    </g>
    {/* Handle */}
    <rect x="42" y="76" width="12" height="3" rx="1.5" fill="#cfa562"/>
  </svg>
);

const TableIcon = (
  <svg viewBox="0 0 96 96" width="96" height="96" fill="none" aria-hidden="true">
    {/* Plate */}
    <circle cx="48" cy="52" r="30" fill="#fff8ec" stroke="#8a7461" strokeWidth="2"/>
    <circle cx="48" cy="52" r="22" fill="#f5e6c8" stroke="#8a7461" strokeWidth="1"/>
    {/* Food */}
    <ellipse cx="48" cy="52" rx="14" ry="10" fill="#B45A37"/>
    <ellipse cx="48" cy="49" rx="10" ry="6" fill="#e8a828"/>
    <ellipse cx="46" cy="47" rx="3" ry="1.5" fill="#fff8ec" opacity="0.5"/>
    {/* Sparkle */}
    <path d="M78 22 L78 30 M74 26 L82 26" stroke="#B45A37" strokeWidth="2" strokeLinecap="round"/>
    <path d="M22 74 L22 82 M18 78 L26 78" stroke="#B45A37" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="16" cy="22" r="1.5" fill="#B45A37"/>
    <circle cx="80" cy="72" r="1.5" fill="#B45A37"/>
  </svg>
);

// ------------------------------------------------------ Steps ------------
const STEPS = [
  {
    title: "Welcome home.",
    body: (
      <>
        A thoughtful home starts with a warm setup. Five short steps, and
        HearthHouse begins to feel like yours. You can come back to any of
        these later — nothing here is one-shot.
      </>
    ),
    cta: "Let's begin",
    href: null,
    illustration: HomeIcon,
  },
  {
    title: "Tell us how you cook.",
    body: (
      <>
        Pick the cuisines your family cooks — Indian, Italian, Thai — and
        the regional styles you love (Punjabi, Bengali, Tuscan). The Family
        Table uses this so recipe suggestions sound like your kitchen, not
        a random blog.
      </>
    ),
    cta: "Set my cuisines",
    href: "/setup/cuisines",
    illustration: CuisineIcon,
  },
  {
    title: "Save your local shops.",
    body: (
      <>
        Add the supermarkets, butchers and bakers you actually use. Type
        "Woolworths near Bondi" and HearthHouse pulls in the branch
        details. Then, every time you refill something, we quietly learn
        which shop you buy it from — and pre-select it next time.
      </>
    ),
    cta: "Add my first shop",
    href: "/setup/vendors",
    illustration: ShopIcon,
  },
  {
    title: "Fill the pantry drawer.",
    body: (
      <>
        Load a regional spice preset — dozens of bowls appear in your
        drawer instantly. Tap the ones you actually have to top them up;
        leave the empty ones as a gentle shopping list. HearthHouse
        assumes anything in this drawer is on-hand when suggesting recipes.
      </>
    ),
    cta: "Pick a pantry preset",
    href: "/setup/staples",
    illustration: PantryIcon,
  },
  {
    title: "Cook something tonight.",
    body: (
      <>
        You're all set. The Family Table can now find rescue recipes for
        things about to expire, or gently surprise you with a dish from an
        adjacent cuisine you'd love. Welcome to HearthHouse.
      </>
    ),
    cta: "Take me to the Family Table",
    href: "/family-table",
    illustration: TableIcon,
  },
];
